import React, { useEffect, useState } from "react";
import { Button, Empty, Row, Select, Table, Tag, DatePicker, notification } from "antd";
import { EyeFilled, RedoOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useLoading } from "../../context/loadingContext";
import GetMerchantSettle from "../../functions/merchant/getMerchantSettle";
import moneyFormat from "../../functions/utils/moneyFormat";
import SettleTransactions from "../../components/modal/settleTransactions";
import TablePagination from "components/pagination/tablePagination";
import { bg_button, bg_shadow } from "constant/css";

const { RangePicker } = DatePicker;

const TxCounts = ({ settled, pending }) => (
  <span
    className="expense-tx-counts"
    title={`Шилжүүлсэн ${settled}, үлдсэн ${pending}`}
  >
    <span className={settled ? "expense-tx-ok" : "expense-tx-zero"}>{settled}</span>
    <span className={pending ? "expense-tx-wait" : "expense-tx-zero"}>{pending}</span>
  </span>
);

const createdAtQuery = (dates) => {
  if (!dates?.[0] || !dates?.[1]) return "";
  const value = dates
    .map((date, index) =>
      index === 0
        ? dayjs(date).startOf("day").format("YYYY-MM-DD HH:MM")
        : dayjs(date).endOf("day").format("YYYY-MM-DD HH:MM")
    )
    .join("->");
  return `&createdAt=${value}`;
};

const ExpenseTransactions = ({ merchants, active }) => {
  const { showLoading, hideLoading } = useLoading();
  const [merchantId, setMerchantId] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [paginationData, setPaginationData] = useState({ pageSize: 20, total: 0 });
  const [showTransactions, setShowTransactions] = useState(null);
  const [settleSettledAt, setSettleSettledAt] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [totals, setTotals] = useState({
    totalAmount: 0,
    totalMerchant: 0,
    totalXpay: 0,
    totalSubMerchant: 0,
    totalProcessingAmount: 0,
    totalFailed: 0,
    totalSuccess: 0,
  });

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const load = async () => {
      if (!merchantId) {
        setData([]);
        setPaginationData({ pageSize, total: 0 });
        setTotals({
          totalAmount: 0,
          totalMerchant: 0,
          totalXpay: 0,
          totalSubMerchant: 0,
          totalProcessingAmount: 0,
          totalFailed: 0,
          totalSuccess: 0,
        });
        return;
      }
      showLoading();
      try {
        const response = await GetMerchantSettle(
          merchantId,
          `&page=${page}&size=${pageSize}${createdAtQuery(createdAt)}`
        );
        if (cancelled || !response) return;
        const rows = response.data || [];
        const totalFailed = rows.reduce(
          (acc, row) =>
            acc + (row.transactions || []).filter((t) => t.status !== "SETTLED").length,
          0
        );
        const totalSuccess = rows.reduce(
          (acc, row) =>
            acc + (row.transactions || []).filter((t) => t.status === "SETTLED").length,
          0
        );
        setData(rows);
        setPaginationData(response.pagination || { pageSize, total: 0 });
        setTotals({
          totalAmount: response.totalAmount || 0,
          totalMerchant: response.totalMerchant || 0,
          totalXpay: response.totalXpay || 0,
          totalSubMerchant: response.totalSubMerchantAmount || 0,
          totalProcessingAmount: response.totalProcessingAmount || 0,
          totalFailed,
          totalSuccess,
        });
      } catch (err) {
        if (!cancelled) {
          setData([]);
          notification.error({
            message:
              err.response?.data?.message ||
              err.message ||
              "Зарлагын гүйлгээ авахад алдаа гарлаа",
          });
        }
      } finally {
        hideLoading();
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // showLoading/hideLoading are stable context helpers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, merchantId, createdAt, page, pageSize, refreshKey]);

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 70,
    },
    {
      title: "Нийт дүн",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (value) => moneyFormat(value),
    },
    {
      title: "Тоо/ш",
      dataIndex: "count",
      key: "count",
      align: "right",
    },
    {
      title: "Дэд мерчант дүн",
      dataIndex: "subMerchantAmount",
      key: "subMerchantAmount",
      align: "right",
      render: (value) => moneyFormat(value),
    },
    {
      title: "Мерчант дүн",
      dataIndex: "merchantAmount",
      key: "merchantAmount",
      align: "right",
      render: (value) => moneyFormat(value),
    },
    {
      title: "xPay авах дүн",
      dataIndex: "xpayAmount",
      key: "xpayAmount",
      align: "right",
      render: (value) => moneyFormat(value),
    },
    {
      title: "Процэсс дүн",
      dataIndex: "processingAmount",
      key: "processingAmount",
      align: "right",
      render: (value) => (
        <span className="text-orange-700">{moneyFormat(value)}</span>
      ),
    },
    {
      title: "Үүссэн огноо",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Төрөл",
      dataIndex: "settledType",
      key: "settledType",
      render: (value) => value || "-",
    },
    {
      title: "Төлөв",
      dataIndex: "status",
      key: "status",
      render: (value) =>
        value === "SETTLED" ? (
          <Tag color="cyan">Өндөрлөсөн</Tag>
        ) : value === "ACTIVE" ? (
          <Tag color="blue">Идэвхтэй</Tag>
        ) : (
          <Tag>Идэвхгүй</Tag>
        ),
    },
    {
      title: (
        <span className="expense-tx-head">
          Гүйлгээ
          <span className="expense-tx-head__meta">
            <span>шилжүүлсэн</span>
            <span>үлдсэн</span>
          </span>
        </span>
      ),
      dataIndex: "transactions",
      key: "transactions",
      width: 148,
      align: "right",
      render: (rows) => (
        <TxCounts
          settled={(rows || []).filter((row) => row.status === "SETTLED").length}
          pending={(rows || []).filter((row) => row.status !== "SETTLED").length}
        />
      ),
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      fixed: "right",
      width: 40,
      render: (_, row) => (
        <Button
          size="small"
          onClick={() => {
            setSettleSettledAt(row.settledAt || null);
            setShowTransactions(row.id);
          }}
          icon={<EyeFilled style={{ color: "green", fontSize: 14 }} />}
        />
      ),
    },
  ];

  return (
    <div>
      <Row style={{ gap: 10 }}>
        <Button
          type="primary"
          className={`${bg_button} ${bg_shadow} rounded-xl`}
          icon={<RedoOutlined />}
          onClick={() => {
            setCreatedAt(null);
            setPage(1);
            setRefreshKey((value) => value + 1);
          }}
        />
      </Row>
      <Row className="invoice-control-search" align="middle">
        <RangePicker
          size="small"
          value={createdAt}
          onChange={(dates) => {
            setPage(1);
            setCreatedAt(dates && dates[0] && dates[1] ? dates : null);
          }}
          allowClear
          placeholder={["Үүссэн огноо", "Дуусах"]}
        />
        <Select
          size="small"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Бизнесийн нэр"
          style={{ width: 220 }}
          value={merchantId || undefined}
          onChange={(value) => {
            setPage(1);
            setMerchantId(value || null);
          }}
          options={merchants}
        />
      </Row>
      {!merchantId ? (
        <div className="tableContainer">
          <Empty description="Бизнес сонгоно уу" />
        </div>
      ) : (
        <div className="tableContainer">
          <Table
            className="invoice-control-table expense-settle-table"
            size="small"
            columns={columns}
            rowKey={(record) => record.id}
            scroll={{ x: "max-content", y: "calc(100vh - 38vh)" }}
            pagination={false}
            dataSource={data}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row className="summaryRow">
                  <Table.Summary.Cell index={0} />
                  <Table.Summary.Cell index={1} align="right">
                    <span className="expense-total">{moneyFormat(totals.totalAmount)}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} align="right">
                    {moneyFormat(totals.totalSubMerchant)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    {moneyFormat(totals.totalMerchant)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    {moneyFormat(totals.totalXpay)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <span className="expense-processing">
                      {moneyFormat(totals.totalProcessingAmount)}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} />
                  <Table.Summary.Cell index={8} />
                  <Table.Summary.Cell index={9} />
                  <Table.Summary.Cell index={10} align="right" className="expense-tx-summary">
                    <TxCounts settled={totals.totalSuccess} pending={totals.totalFailed} />
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={11} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          <TablePagination
            page={page}
            paginationData={paginationData}
            changePage={setPage}
            changeSize={(_, size) => {
              setPage(1);
              setPageSize(size);
            }}
          />
        </div>
      )}
      {showTransactions && merchantId ? (
        <SettleTransactions
          open={showTransactions}
          refresh={() => setRefreshKey((value) => value + 1)}
          close={() => {
            setShowTransactions(null);
            setSettleSettledAt(null);
          }}
          defaultMerchantId={merchantId}
          settledAt={settleSettledAt}
        />
      ) : null}
    </div>
  );
};

export default ExpenseTransactions;
